import { Group } from "@dinahmoe/dmafjs-visual";
import { coreEngine, nodes, env } from "@dinahmoe/dmafjs-core";

class Header extends Group {
  namespace = "visual:header";

  handlers = {
    'size.width': this.handleWidthChange,
    'size.height': this.handleContentSmootherY,
    contentSmootherY: this.handleContentSmootherY
  };

  handleWidthChange(node) {
    const width = node['size.width'];
    const shouldCollapseLogos = width < 870;
    const isMobile = width < 700;

    // Logo breakpoint — collapse/expand based on width
    if (shouldCollapseLogos && !node._logoCollapsed) {
      this.collapseLogoImmediate(node);
      this.collapseSkellefteaImmediate(node);
    } else if (!shouldCollapseLogos && node._widthForcedSmall) {
      // Only expand if scroll position also allows it
      const scrollY = node.contentSmootherY || 0;
      if (scrollY <= 100) {
        this.expandLogoImmediate(node);
        this.expandSkellefteaImmediate(node);
      }
    }
    node._widthForcedSmall = shouldCollapseLogos;

    // Mobile menu breakpoint
    if (node._ids?.menu_container) {
      node._ids.menu_container['appearance.opacity'] = isMobile ? 0 : 1;
    }
    if (node._ids?.mobile_menu_button) {
      node._ids.mobile_menu_button['appearance.opacity'] = isMobile ? 1 : 0;
    }
    if (node._ids?.audio_icon) {
      node._ids.audio_icon['position.x'] = isMobile ? -84 : (node._logoCollapsed ? -32 : -156);
    }
    if (node._ids?.showcase_headline) {
      node._ids.showcase_headline['position.y'] = isMobile ? 32 : 100;
    }

    // Close mobile menu if resized above breakpoint
    if (!isMobile && node._mobileMenuOverlay) {
      this.closeMobileMenu(node);
    }
  }

  handleContentSmootherY(node) {
    const sectionsNode = nodes.get("sections");
    const rootHeight = nodes.get("root")['size.height'];
    const scrollY = node.contentSmootherY;
    const scroll = Math.max(0, scrollY - rootHeight);

    this.handleLogoSwap(node, scrollY);
    this.handleAboutScroll(node, scrollY, sectionsNode);
    this.handleIntroLines(node, scrollY, rootHeight);
    this.handleShowcaseHeadline(node, scrollY, sectionsNode, rootHeight);

    const scrollContent = nodes.get("scroll_content");
    const smoother = nodes.get("content_smoother_y");
    if (scrollContent) {
      // Only set position on desktop — on mobile/tablet, native scroll handles it
      if (smoother) {
        scrollContent['position.y'] = -scrollY;
      }

      const contentHeight = scrollContent['size.height'];
      if (contentHeight > 0 && smoother) {
        smoother["value.max"] = contentHeight - rootHeight;
      }
    }

    const scrollButton = node._ids?.scroll_button;
    if (scrollButton) {
      const smoother = nodes.get("content_smoother_y");
      if (smoother) {
        const maxScroll = smoother["value.max"];
        if (maxScroll > 0) {
          const fadeDistance = 200;
          const remaining = maxScroll - scrollY;
          scrollButton['appearance.opacity'] = remaining > fadeDistance ? 1 : Math.max(0, remaining / fadeDistance);
        }
      }
    }

    node.gradientScrollY = scrollY;
  }

  collapseLogoImmediate(node) {
    const logoGroup = node._ids?.logo_group;
    const logoImage = node._ids?.logo;
    if (!logoGroup || !logoImage) return;
    node._logoCollapsed = true;
    logoGroup['size.width'] = 55;
    logoImage['source.imageId'] = 'logo_small';
    logoImage['size.width'] = 55;
    logoImage['position.x'] = -1;
  }

  expandLogoImmediate(node) {
    const logoGroup = node._ids?.logo_group;
    const logoImage = node._ids?.logo;
    if (!logoGroup || !logoImage) return;
    node._logoCollapsed = false;
    logoImage['source.imageId'] = 'logo';
    logoImage['size.width'] = 191;
    logoImage['position.x'] = 0;
    logoGroup['size.width'] = 191;
  }

  collapseSkellefteaImmediate(node) {
    const skelGroup = node._ids?.skelleftea_group;
    if (skelGroup) skelGroup['size.width'] = 0;
    if (node._ids?.audio_icon) {
      node._ids.audio_icon['position.x'] = -32;
    }
  }

  expandSkellefteaImmediate(node) {
    const skelGroup = node._ids?.skelleftea_group;
    if (skelGroup) skelGroup['size.width'] = 100;
    if (node._ids?.audio_icon) {
      node._ids.audio_icon['position.x'] = -156;
    }
  }

  handleLogoSwap(node, scrollY) {
    // Width already forced small — don't animate
    if (node._widthForcedSmall) return;

    const threshold = 100;
    const shouldCollapse = scrollY > threshold;

    if (shouldCollapse && !node._logoCollapsed) {
      if (env.motion === "reduced") {
        this.collapseLogoImmediate(node);
        this.collapseSkellefteaImmediate(node);
      } else {
        const logoGroup = node._ids?.logo_group;
        const logoImage = node._ids?.logo;
        if (!logoGroup || !logoImage) return;
        const duration = 200;
        node._logoCollapsed = true;
        coreEngine.update({
          uid: logoGroup.uid, key: "transform",
          value: { transforms: [{ property: "size.width", valueEnd: 55, duration, easing: "EASE_IN_OUT" }] }
        });
        setTimeout(() => {
          logoImage['source.imageId'] = 'logo_small';
          logoImage['size.height'] = 72;
          logoImage['position.x'] = -1;
        }, duration);
        this.collapseSkellefteaLogo(node, duration);
      }
    } else if (!shouldCollapse && node._logoCollapsed) {
      if (env.motion === "reduced") {
        this.expandLogoImmediate(node);
        this.expandSkellefteaImmediate(node);
      } else {
        const logoGroup = node._ids?.logo_group;
        const logoImage = node._ids?.logo;
        if (!logoGroup || !logoImage) return;
        const duration = 200;
        node._logoCollapsed = false;
        logoImage['source.imageId'] = 'logo';
        logoImage['size.height'] = 72;
        logoImage['position.x'] = 0;
        coreEngine.update({
          uid: logoGroup.uid, key: "transform",
          value: { transforms: [{ property: "size.width", valueEnd: 191, duration, easing: "EASE_IN_OUT" }] }
        });
        this.expandSkellefteaLogo(node, duration);
      }
    }
  }

  collapseSkellefteaLogo(node, duration) {
    const skelGroup = node._ids?.skelleftea_group;
    const audioIcon = node._ids?.audio_icon;
    if (skelGroup) {
      coreEngine.update({
        uid: skelGroup.uid, key: "transform",
        value: { transforms: [{ property: "size.width", valueEnd: 0, duration, easing: "EASE_IN_OUT" }] }
      });
    }
    if (audioIcon) {
      coreEngine.update({
        uid: audioIcon.uid, key: "transform",
        value: { transforms: [{ property: "position.x", valueEnd: -32, duration, easing: "EASE_IN_OUT" }] }
      });
    }
  }

  expandSkellefteaLogo(node, duration) {
    const skelGroup = node._ids?.skelleftea_group;
    const audioIcon = node._ids?.audio_icon;
    if (skelGroup) {
      coreEngine.update({
        uid: skelGroup.uid, key: "transform",
        value: { transforms: [{ property: "size.width", valueEnd: 100, duration, easing: "EASE_IN_OUT" }] }
      });
    }
    if (audioIcon) {
      coreEngine.update({
        uid: audioIcon.uid, key: "transform",
        value: { transforms: [{ property: "position.x", valueEnd: -156, duration, easing: "EASE_IN_OUT" }] }
      });
    }
  }

  handleIntroLines(node, scrollY, rootHeight) {
    const headline = node._ids?.intro_headline;
    const group = node._ids?.intro_lines;
    const scrollText = node._ids?.scroll_text;
    if (!group) return;

    // Staggered scroll — headline departs first, scroll text last, all at normal speed
    const groupStart = rootHeight * 0.10;
    const scrollTextStart = rootHeight * 0.20;

    if (env.motion !== "reduced") {
      if (headline) headline['position.y'] = -150 - scrollY;
      group['position.y'] = -Math.max(0, scrollY - groupStart);
      if (scrollText) scrollText['position.y'] = -110 - Math.max(0, scrollY - scrollTextStart);
    }

    // Staggered opacity fade — only after intro animations have started
    if (node._introAnimationStarted) {
      const fadeRange = rootHeight * 0.25;

      if (headline) {
        const fadeStart = rootHeight * 0.05;
        if (scrollY > fadeStart) headline['appearance.opacity'] = Math.max(0, 1 - (scrollY - fadeStart) / fadeRange);
        else if (env.motion === "reduced") headline['appearance.opacity'] = 1;
      }

      const groupFadeStart = rootHeight * 0.10;
      if (scrollY > groupFadeStart) group['appearance.opacity'] = Math.max(0, 1 - (scrollY - groupFadeStart) / fadeRange);
      else if (env.motion === "reduced") group['appearance.opacity'] = 1;

      if (scrollText) {
        const fadeStart = rootHeight * 0.15;
        if (scrollY > fadeStart) scrollText['appearance.opacity'] = Math.max(0, 1 - (scrollY - fadeStart) / fadeRange);
        else if (env.motion === "reduced") scrollText['appearance.opacity'] = 1;
      }
    }
  }

  handleShowcaseHeadline(node, scrollY, sectionsNode, rootHeight) {
    const headline = node._ids?.showcase_headline;
    if (!headline) return;

    const showcaseStart = sectionsNode.showcase - rootHeight / 1.5;
    const showcaseEnd = showcaseStart + rootHeight / 1.5;
    const inRange = scrollY >= showcaseStart && scrollY < showcaseEnd;
    const shouldShow = inRange;
    const shouldShowSubtitle = inRange && !node._showcaseHeadlineDismissed;

    if (shouldShow && !node._showcaseHeadlineVisible) {
      node._showcaseHeadlineVisible = true;
      clearTimeout(node._headlineFadeInTimer);
      node._headlineFadeInTimer = setTimeout(() => {
        if (!node._showcaseHeadlineVisible) return;
        coreEngine.update({
          uid: headline.uid,
          key: 'transform',
          value: { transforms: [{ property: 'appearance.opacity', valueEnd: 1, duration: 600, easing: 'EASE_OUT' }] }
        });
        const subtitle = node._ids?.showcase_subtitle;
        if (subtitle && !node._showcaseHeadlineDismissed) {
          coreEngine.update({
            uid: subtitle.uid,
            key: 'transform',
            value: { transforms: [{ property: 'appearance.opacity', valueEnd: 1, duration: 600, easing: 'EASE_OUT' }] }
          });
        }
      }, 500);
    } else if (!shouldShow && node._showcaseHeadlineVisible) {
      node._showcaseHeadlineVisible = false;
      clearTimeout(node._headlineFadeInTimer);
      coreEngine.update({
        uid: headline.uid,
        key: 'transform',
        value: { transforms: [{ property: 'appearance.opacity', valueEnd: 0, duration: 600, easing: 'EASE_IN' }] }
      });
    }

    // Fade subtitle independently (fade-out only — fade-in handled above)
    const subtitle = node._ids?.showcase_subtitle;
    if (subtitle) {
      if (!shouldShowSubtitle && node._showcaseSubtitleVisible) {
        node._showcaseSubtitleVisible = false;
        coreEngine.update({
          uid: subtitle.uid,
          key: 'transform',
          value: { transforms: [{ property: 'appearance.opacity', valueEnd: 0, duration: 600, easing: 'EASE_IN' }] }
        });
      } else if (shouldShowSubtitle) {
        node._showcaseSubtitleVisible = true;
      }
    }
  }

  init(node, event) {
    super.init(node, event);
    node._logoCollapsed = false;
    node._widthForcedSmall = false;
    node._aboutOpen = false;
    node._mobileMenuOverlay = null;
    node._introAnimationStarted = false;
    node._showcaseHeadlineVisible = false;
    node._caseStoryOpen = false;
    node._showcaseHeadlineDismissed = false;
    this.setupTextActions();
    this.handleContentSmootherY(node);
    this.fadeInIntroLines(node);
  }

  setupTextActions() {
    document.addEventListener("click", (e) => {
      const action = e.target.closest("[data-action]")?.dataset.action;
      if (!action) return;
      e.preventDefault();
      if (action === "about") {
        this.dispatch("about:open");
      } else if (action === "dive-in") {
        this.dispatch("scroll_button:next_section");
      }
    });
  }

  fadeInIntroLines(node) {
    node._introAnimationStarted = true;

    // Don't animate intro text if deep linked to a non-home route or reduced motion
    const scrollY = node.contentSmootherY || 0;
    if (scrollY > 0) return;
    const nav = nodes.get("se26_navigation");
    if (nav && nav.pathname !== "/") return;
    if (env.motion === "reduced") return;

    const groups = [
      node._ids?.intro_headline,
      node._ids?.intro_lines,
      node._ids?.scroll_text
    ];
    const delayBetween = 500;
    const fadeDuration = 1000;

    groups.forEach((el, i) => {
      if (!el) return;
      coreEngine.update({
        uid: el.uid,
        key: 'transform',
        value: { transforms: [{
          property: 'appearance.opacity',
          valueStart: 0, valueEnd: 1,
          duration: fadeDuration,
          delay: i * delayBetween,
          easing: 'EASE_OUT'
        }] }
      });
    });
  }

  state(node, config, event) {
    const parts = event.name.split(":");
    const action = parts[parts.length - 1];

    if (action === "open" && event.name.startsWith("about")) {
      nodes.get("container_case_story")?.tell({
        type: "open_about",
        sourceUid: node.uid
      });
    } else if (event.name === "showcase:category_clicked" || event.name === "case_story:open") {
      node._caseStoryOpen = true;
      node._showcaseHeadlineDismissed = true;
      this.handleShowcaseHeadline(node, node.contentSmootherY, nodes.get("sections"), nodes.get("root")['size.height']);
    } else if (event.name === "case_story:opened" && event.data?.type === "about") {
      node._aboutOpen = true;
      node._aboutOpenScrollY = node.contentSmootherY;
    } else if (event.name === "case_story:closed") {
      node._aboutOpen = false;
      node._caseStoryOpen = false;
      this.handleShowcaseHeadline(node, node.contentSmootherY, nodes.get("sections"), nodes.get("root")['size.height']);
    } else if (event.name.startsWith("se26_navigation:navigate")) {
      if (node._mobileMenuOverlay) {
        this.closeMobileMenu(node);
      }
    } else if (event.name === "mobile_menu:toggle") {
      if (node._mobileMenuOverlay) {
        this.closeMobileMenu(node);
      } else {
        this.openMobileMenu(node);
      }
    }
  }

  openMobileMenu(node) {
    const icon = node._ids?.mobile_menu_icon;
    if (icon) icon['source.imageId'] = 'menu-mobile-close';

    const root = nodes.get("root");
    const rootHeight = root["size.height"];

    const navNode = nodes.get("se26_navigation");
    const routes = navNode?.routes;
    if (!routes) return;

    const menuRoutes = routes.filter(r => {
      if (r.action && r.action !== "navigate") return false;
      if (r.submenu) return false;
      if (!r.label) return false;
      return true;
    });

    const menuItems = menuRoutes.map(route => ({
      namespace: "visual:group",
      size: { width: "100%", height: 60 },
      events: [{
        type: "click",
        routeId: route.id,
        navigationUid: "se26_navigation",
        data: { source: "mobile_menu" }
      }],
      children: [{
        namespace: "visual:text",
        font: { size: 36, family: "n27" },
        text: { content: route.label, color: "#FFFFFF", align: "left" }
      }]
    }));

    node._mobileMenuOverlay = coreEngine.create({
      namespace: "visual:group",
      parentUid: "root",
      size: { width: "100%", height: "!{parent.size.height}" },
      position: { y: rootHeight },
      appearance: { background: "#262223" },
      layout: { direction: "column", justifyContent: "space-between" },
      padding: { left: 50, right: 50, top: 120, bottom: 32 },
      children: [
        {
          namespace: "visual:group",
          layout: { direction: "column", gap: 20 },
          children: menuItems
        },
        {
          namespace: "visual:group",
          size: { width: "100%" },
          layout: { direction: "row", alignItems: "center", justifyContent: "space-between" },
          children: [
            {
              namespace: "visual:image",
              size: { width: 97, height: 98 },
              margin: { top: 4 },
              source: { imageId: "eu", alt: "European Union" }
            },
            {
              namespace: "visual:image",
              size: { width: 200, height: 98 },
              source: { imageId: "skelleftea", alt: "Skelleftea municipality" }
            }
          ]
        },
      ]
    }, { key: "create_mobile_menu" });

    node._mobileMenuClose = coreEngine.create({
      namespace: "visual:group",
      parentUid: "root",
      size: { width: 36, height: 36 },
      position: { x: -32, y: 39 },
      anchor: { x: "right" },
      appearance: { opacity: 0 },
      events: [{ type: "click", name: "mobile_menu:toggle" }],
      children: [{
        namespace: "visual:svg",
        size: { width: 24, height: 24 },
        anchor: { x: "center", y: "center" },
        source: { imageId: "menu-mobile-close" }
      }]
    });

    coreEngine.update({
      uid: node._mobileMenuOverlay.uid,
      key: "transform",
      value: { transforms: [{
        property: "position.y",
        valueEnd: 0,
        duration: 600,
        easing: "EASE_IN_OUT"
      }] }
    });

    coreEngine.update({
      uid: node._mobileMenuClose.uid,
      key: "transform",
      value: { transforms: [{
        property: "appearance.opacity",
        valueEnd: 1,
        duration: 300,
        delay: 400,
        easing: "EASE_OUT"
      }] }
    });
  }

  closeMobileMenu(node) {
    const icon = node._ids?.mobile_menu_icon;
    if (icon) icon['source.imageId'] = 'menu-mobile';

    if (node._mobileMenuOverlay) {
      const overlayUid = node._mobileMenuOverlay.uid;
      const rootHeight = nodes.get("root")["size.height"];
      node._mobileMenuOverlay = null;

      if (node._mobileMenuClose) {
        coreEngine.destroy(node._mobileMenuClose.uid);
        node._mobileMenuClose = null;
      }

      coreEngine.update({
        uid: overlayUid,
        key: "transform",
        value: { transforms: [{
          property: "position.y",
          valueEnd: rootHeight,
          duration: 600,
          easing: "EASE_IN_OUT"
        }] }
      });

      setTimeout(() => coreEngine.destroy(overlayUid), 600);
    }
  }

  handleAboutScroll(node, scrollY, sectionsNode) {
    if (!node._aboutOpen) return;

    const vh = node['size.height'];

    if (Math.abs(scrollY - node._aboutOpenScrollY) < 100) return;

    // Showcase zone: network section visible until connect starts sliding in
    const showcaseStart = sectionsNode.showcase + vh / 2;
    const showcaseEnd = sectionsNode.connect;

    // Lower zone: connect/search section in view, bounded by search content end
    const lowerStart = sectionsNode.connect + vh;
    const connectLayout = nodes.get("layout_connect");
    const searchLayout = nodes.get("layout_search");
    const connectHeight = connectLayout?.['size.height'] || 0;
    const searchHeight = searchLayout?.['size.height'] || 0;
    const lowerEnd = lowerStart + connectHeight + searchHeight;

    const inShowcase = scrollY >= showcaseStart && scrollY <= showcaseEnd;
    const inLower = scrollY >= lowerStart && scrollY <= lowerEnd;

    if (!inShowcase && !inLower) {
      node._aboutOpen = false;
      nodes.get("container_case_story")?.tell({ type: "close" });
    }
  }

  navigateNext(node) {
    const sectionsNode = nodes.get("sections");
    const scrollY = node.contentSmootherY;
    const threshold = 1;

    if (scrollY < sectionsNode.showcase - threshold) {
      this.dispatch("se26_navigation:navigate:showcase");
    } else if (scrollY < sectionsNode.connect - threshold) {
      this.dispatch("se26_navigation:navigate:connect");
    } else {
      this.dispatch("se26_navigation:navigate:download");
    }
  }

  receiveMessage(node, message) {
    if (message.uid === node._ids?.audio_icon?.uid && message.key === "click") {
      const master = nodes.get("audio_master");
      if (master) {
        const muted = !master.muted;
        master.muted = muted;
        node._ids.audio_icon["source.imageId"] = muted ? "audio-off" : "audio-on";
      }
      return;
    }
    if (message.uid === node._ids?.scroll_button?.uid) {
      const arrowsUid = node._ids?.scroll_arrows?.uid;
      if (message.key === "over") {
        coreEngine.update({
          uid: arrowsUid,
          key: "transform",
          value: { transforms: [{
            property: "position.y",
            valueEnd: 0,
            duration: 500,
            easing: "LINEAR"
          }] }
        });
      } else if (message.key === "out") {
        coreEngine.update({
          uid: arrowsUid,
          key: "transform",
          value: { transforms: [{
            property: "position.y",
            valueEnd: -56,
            duration: 500,
            easing: "LINEAR"
          }] }
        });
      }
    }
    super.receiveMessage(node, message);
  }
}

const header = new Header();
export { header, Header };
